import apiClient from './apiClient';

export interface Contact {
  id: string;
  accountId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  role?: string;
  department?: string;
  currentStage?: string;
  lastCompletedStage?: string;
  nextStageDue?: string;
  engagementScore: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  account?: {
    id: string;
    name: string;
    domain?: string;
    industryVertical?: string;
    region?: string;
    abmTier?: number;
    status: string;
  };
  is_decision_maker?: boolean;
  linkedin_url?: string;
}

export interface ContactsResponse {
  data: Contact[];
  total: number;
  page: number;
  totalPages: number;
}

export interface CreateContactData {
  accountId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  role?: string;
  department?: string;
  is_decision_maker?: boolean;
  linkedin_url?: string;
}

export interface TimelineEvent {
  id: string;
  type: 'CALL' | 'MAGNET_DELIVERY' | 'ENGAGEMENT';
  subType?: string;
  timestamp: string;
  title: string;
  description: string;
  contactId?: string;
  contactName?: string;
  metadata: Record<string, any>;
  icon: string;
  color: string;
}

export interface TimelineResponse {
  data: TimelineEvent[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary: {
    totalCalls: number;
    totalDeliveries: number;
    totalEngagements: number;
    totalEvents: number;
  };
}

/**
 * Get all contacts with optional filters
 */
export const getContacts = async (params?: {
  account_id?: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<ContactsResponse> => {
  const response = await apiClient.get<ContactsResponse>('/api/contacts', { params });
  return response.data;
};

/**
 * Get single contact by ID
 */
export const getContact = async (id: string): Promise<Contact> => {
  const response = await apiClient.get<Contact>(`/api/contacts/${id}`);
  return response.data;
};

/**
 * Get contact interaction timeline
 */
export const getContactTimeline = async (
  id: string,
  params?: {
    page?: number;
    limit?: number;
  }
): Promise<TimelineResponse> => {
  const response = await apiClient.get<TimelineResponse>(`/api/contacts/${id}/timeline`, { params });
  return response.data;
};

/**
 * Create new contact
 */
export const createContact = async (data: CreateContactData): Promise<Contact> => {
  const response = await apiClient.post<Contact>('/api/contacts', data);
  return response.data;
};

/**
 * Update contact
 */
export const updateContact = async (id: string, data: Partial<CreateContactData>): Promise<Contact> => {
  const response = await apiClient.put<Contact>(`/api/contacts/${id}`, data);
  return response.data;
};

/**
 * Delete contact
 */
export const deleteContact = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/contacts/${id}`);
};
